import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api/axios';
import { Trophy, Crown, Medal, ChartBar, CurrencyDollar, ShoppingCart, ChatCircleDots, Target, MapPin, Share, DownloadSimple } from '@phosphor-icons/react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';

const PERIODS = [
  { key: 'today', label: 'Hoy' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mes' },
  { key: 'year', label: 'Año' },
];

const Ranking = () => {
  const { selectedBusiness } = useOutletContext();
  const { user: currentUser, isAdmin } = useAuth();
  const canView = isAdmin || currentUser?.role === 'supervisor';
  const [ranking, setRanking] = useState([]);
  const [period, setPeriod] = useState('month');
  const [department, setDepartment] = useState('');
  const [deptList, setDeptList] = useState({ guatemala: [], special: [] });
  const [loading, setLoading] = useState(false);
  const [businessFilter, setBusinessFilter] = useState('current');
  const [sharing, setSharing] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    api.get('/departments').then(r => setDeptList(r.data)).catch(() => {});
  }, []);

  const handleShare = async () => {
    if (!captureRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(captureRef.current, {
        backgroundColor: '#0F172A',
        pixelRatio: 2,
        cacheBust: true,
        filter: (node) => !node.classList || !node.classList.contains('no-share'),
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `top-vendedores-${period}-${Date.now()}.png`, { type: 'image/png' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: '🏆 Top Vendedores',
          text: '¡Mira el ranking de vendedores del mes!',
        });
        toast.success('Ranking compartido');
      } else {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = file.name;
        link.click();
        toast.success('Imagen descargada');
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('No se pudo compartir el ranking');
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    if (canView) fetchRanking();
  }, [period, department, businessFilter, selectedBusiness]);

  const fetchRanking = async () => {
    setLoading(true);
    try {
      const params = { period };
      if (department) params.department = department;
      if (businessFilter === 'current' && selectedBusiness?.id) params.business_id = selectedBusiness.id;
      const response = await api.get('/reports/ranking', { params });
      setRanking(response.data.ranking || []);
    } catch (error) {
      console.error('Error fetching ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!canView) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="ranking-no-access">
        <p className="text-sm text-[var(--text-muted)]">Solo administradores y supervisores pueden ver el ranking.</p>
      </div>
    );
  }

  const top3 = ranking.slice(0, 3);
  const rest = ranking.slice(3);
  const formatMoney = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-[var(--bg)] p-4 md:p-8" data-testid="ranking-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Trophy size={32} weight="fill" className="text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.5)]" />
            <h1 className="text-2xl md:text-4xl font-black text-[var(--text)] tracking-tight">Top Vendedores</h1>
          </div>
          <p className="text-sm text-[var(--text-muted)] font-medium">Ranking competitivo por dinero generado, pedidos cerrados y conversión.</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="flex items-center gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-1">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[8px] transition-all ${
                  period === p.key
                    ? 'bg-[#1D4ED8] text-white shadow-[0_0_10px_rgba(29,78,216,0.4)]'
                    : 'text-[var(--text-muted)] hover:text-white'
                }`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <select
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            className="text-xs font-bold uppercase bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] px-3 py-2 rounded-[10px] focus:outline-none focus:border-[#1D4ED8]"
            data-testid="filter-department"
          >
            <option value="">Todos los Deptos</option>
            <optgroup label="Guatemala">
              {deptList.guatemala.map(d => <option key={d} value={d}>{d}</option>)}
            </optgroup>
            <optgroup label="Otros">
              {deptList.special.map(d => <option key={d} value={d}>{d}</option>)}
            </optgroup>
          </select>
          <select
            value={businessFilter}
            onChange={(e) => setBusinessFilter(e.target.value)}
            className="text-xs font-bold uppercase bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] px-3 py-2 rounded-[10px] focus:outline-none focus:border-[#1D4ED8]"
            data-testid="filter-business-scope"
          >
            <option value="current">Negocio actual</option>
            <option value="all">Todos los negocios</option>
          </select>
          <button
            onClick={handleShare}
            disabled={sharing || ranking.length === 0}
            className="ml-auto flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-[10px] bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.4)] hover:shadow-[0_0_25px_rgba(16,185,129,0.6)] hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            data-testid="share-ranking-btn"
            title="Compartir ranking como imagen"
          >
            <Share size={16} weight="bold" />
            {sharing ? 'Generando...' : 'Compartir'}
          </button>
        </div>

        {loading ? (
          <div className="text-center text-[var(--text-muted)] py-12">Cargando ranking...</div>
        ) : ranking.length === 0 ? (
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] p-12 text-center" data-testid="ranking-empty">
            <Trophy size={48} weight="duotone" className="text-slate-600 mx-auto mb-3" />
            <p className="text-[var(--text-muted)] font-medium">No hay datos para este período</p>
            <p className="text-xs text-slate-600 mt-1">Aún no se registran pedidos o mensajes en el rango seleccionado.</p>
          </div>
        ) : (
          <div ref={captureRef} className="p-4 md:p-6 bg-[var(--bg)] rounded-[16px]" data-testid="ranking-capture">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl md:text-2xl font-black text-[var(--text)] tracking-tight flex items-center gap-2">
                  <Trophy size={24} weight="fill" className="text-yellow-400" />
                  Top Vendedores · {PERIODS.find(p => p.key === period)?.label}
                </h2>
                <p className="text-xs text-[var(--text-muted)] font-medium mt-0.5">
                  {selectedBusiness?.name || 'Todos los negocios'} {department ? `· ${department}` : ''}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] font-bold">
                {new Date().toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>
            {/* Podium */}
            {top3.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 items-end" data-testid="podium">
                {/* #2 — left on desktop */}
                <PodiumCard data={top3[1]} rank={2} formatMoney={formatMoney} className="md:order-1" />
                {/* #1 — center bigger */}
                <PodiumCard data={top3[0]} rank={1} formatMoney={formatMoney} className="md:order-2 md:scale-110 md:-mt-4" />
                {/* #3 — right */}
                <PodiumCard data={top3[2]} rank={3} formatMoney={formatMoney} className="md:order-3" />
              </div>
            )}

            {/* Rest of the table */}
            {rest.length > 0 && (
              <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[16px] overflow-hidden">
                <div className="p-4 border-b border-[var(--border)] flex items-center gap-2">
                  <ChartBar size={18} weight="bold" className="text-[#1D4ED8]" />
                  <h2 className="text-sm font-black text-[var(--text)] uppercase tracking-wider">Resto del Equipo</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="ranking-table">
                    <thead className="bg-[var(--bg)]">
                      <tr>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">#</th>
                        <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Vendedor</th>
                        <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Dinero</th>
                        <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Pedidos</th>
                        <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Chats</th>
                        <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Mensajes</th>
                        <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Conversión</th>
                        <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-wider text-[var(--text-muted)]">Ticket Prom.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rest.map((r) => (
                        <tr key={r.user_id} className="border-t border-[var(--border)] hover:bg-white/5 transition-colors" data-testid={`ranking-row-${r.user_id}`}>
                          <td className="px-4 py-3 text-sm font-black text-[var(--text-muted)]">{r.rank}</td>
                          <td className="px-4 py-3 text-sm font-bold text-[var(--text)]">{r.name}</td>
                          <td className="px-4 py-3 text-sm font-bold text-emerald-400 text-right">{formatMoney(r.revenue)}</td>
                          <td className="px-4 py-3 text-sm font-bold text-[var(--text)] text-right">{r.orders_closed}</td>
                          <td className="px-4 py-3 text-sm text-[var(--text)] text-right">{r.conversations_attended}</td>
                          <td className="px-4 py-3 text-sm text-[var(--text)] text-right">{r.messages_sent}</td>
                          <td className="px-4 py-3 text-sm font-bold text-[#1D4ED8] text-right">{r.conversion_rate}%</td>
                          <td className="px-4 py-3 text-sm text-[var(--text)] text-right">{formatMoney(r.avg_ticket)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PodiumCard = ({ data, rank, formatMoney, className = '' }) => {
  if (!data) return <div className={className} />;
  const rankConfig = {
    1: { icon: Crown, color: 'yellow', label: '🥇 #1', glow: 'shadow-[0_0_40px_rgba(16,185,129,0.6)]', border: 'border-emerald-400', bg: 'from-emerald-500/20 to-[#111827]' },
    2: { icon: Medal, color: 'slate', label: '🥈 #2', glow: 'shadow-[0_0_20px_rgba(148,163,184,0.3)]', border: 'border-slate-500', bg: 'from-slate-500/10 to-[#111827]' },
    3: { icon: Medal, color: 'orange', label: '🥉 #3', glow: 'shadow-[0_0_20px_rgba(249,115,22,0.3)]', border: 'border-orange-600', bg: 'from-orange-600/10 to-[#111827]' },
  };
  const cfg = rankConfig[rank];
  const Icon = cfg.icon;
  return (
    <div
      className={`bg-gradient-to-b ${cfg.bg} border-2 ${cfg.border} rounded-[20px] p-6 ${cfg.glow} ${className}`}
      data-testid={`podium-${rank}`}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-black uppercase tracking-wider text-[var(--text)]">{cfg.label}</span>
        <Icon size={28} weight="fill" className={rank === 1 ? 'text-yellow-400' : rank === 2 ? 'text-[var(--text)]' : 'text-orange-400'} />
      </div>
      <h3 className="text-xl md:text-2xl font-black text-[var(--text)] mb-1 truncate" data-testid={`podium-name-${rank}`}>{data.name}</h3>
      <p className="text-3xl md:text-4xl font-black text-emerald-400 mb-4 drop-shadow-[0_0_10px_rgba(16,185,129,0.4)]">{formatMoney(data.revenue)}</p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Stat icon={ShoppingCart} label="Pedidos" value={data.orders_closed} />
        <Stat icon={ChatCircleDots} label="Mensajes" value={data.messages_sent} />
        <Stat icon={Target} label="Conversión" value={`${data.conversion_rate}%`} />
        <Stat icon={CurrencyDollar} label="Ticket Prom" value={formatMoney(data.avg_ticket)} />
      </div>
      {data.departments?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[var(--border)] flex items-start gap-1.5">
          <MapPin size={12} weight="bold" className="text-[var(--text-muted)] mt-0.5 shrink-0" />
          <div className="flex flex-wrap gap-1">
            {data.departments.slice(0, 3).map(d => (
              <span key={d} className="text-[9px] font-bold px-1.5 py-0.5 bg-[#1D4ED8]/20 text-[#1D4ED8] border border-[#1D4ED8]/30 rounded">
                {d}
              </span>
            ))}
            {data.departments.length > 3 && (
              <span className="text-[9px] font-bold text-[var(--text-muted)]">+{data.departments.length - 3}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="bg-[var(--bg)]/50 border border-[var(--border)] rounded-[8px] p-2">
    <div className="flex items-center gap-1 mb-0.5">
      <Icon size={10} weight="bold" className="text-[var(--text-muted)]" />
      <span className="text-[9px] uppercase tracking-wide text-[var(--text-muted)] font-bold">{label}</span>
    </div>
    <p className="text-sm font-black text-[var(--text)] truncate">{value}</p>
  </div>
);

export default Ranking;
